import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { DollarSign, TrendingUp, Receipt, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; } };

export default function AdminBrandRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/brand-revenue`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ totals: { all_time: 0, this_month: 0, count: 0 }, brands: [], recent: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <AdminLayout><div className="py-20 flex justify-center"><div className="w-10 h-10 border-4 border-[#94A3B8] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  const t = data.totals;
  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="brand-revenue-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1C]">Brand Revenue</h1>
          <p className="text-sm text-[#475569]">Stripe income per brand — coaching packages, paid sessions, and payment links.</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={<DollarSign size={18} />} label="All-time revenue" value={money(t.all_time)} color="#1D4ED8" testid="stat-alltime" />
          <StatCard icon={<TrendingUp size={18} />} label="This month" value={money(t.this_month)} color="#059669" testid="stat-month" />
          <StatCard icon={<Receipt size={18} />} label="Paid transactions" value={t.count} color="#F97316" testid="stat-count" />
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h2 className="font-bold text-[#0A0F1C] mb-4 flex items-center gap-2"><Building2 size={16} /> By Brand</h2>
          {data.brands.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">No paid transactions yet.</p>
          ) : (
            <div className="space-y-3">
              {data.brands.map((b) => (
                <div key={b.brand_slug} className="border border-[#E2E8F0] rounded-lg p-4" data-testid={`brand-row-${b.brand_slug}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-bold text-[#0A0F1C]">{b.brand_name}</div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-[#475569]">This month: <strong className="text-[#059669]">{money(b.this_month)}</strong></span>
                      <span className="text-[#0A0F1C] font-extrabold">{money(b.all_time)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {b.by_type.map((bt) => (
                      <span key={bt.type} className="text-[11px] bg-[#F1F5F9] text-[#475569] px-2 py-1 rounded-full">{bt.type}: {bt.count} · {money(bt.amount)}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h2 className="font-bold text-[#0A0F1C] mb-4">Recent Transactions</h2>
          {data.recent.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Nothing yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#E2E8F0]">
                  <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Brand</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Item</th><th className="py-2 pr-3">Customer</th><th className="py-2 text-right">Amount</th>
                </tr></thead>
                <tbody>
                  {data.recent.map((r, i) => (
                    <tr key={i} className="border-b border-[#F1F5F9]" data-testid={`txn-row-${i}`}>
                      <td className="py-2 pr-3 text-[#475569] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="py-2 pr-3 text-[#0A0F1C] font-medium">{r.brand_name}</td>
                      <td className="py-2 pr-3 text-[#475569]">{r.type}</td>
                      <td className="py-2 pr-3 text-[#475569] max-w-[220px] truncate">{r.item_name}</td>
                      <td className="py-2 pr-3 text-[#475569]">{r.email || '—'}</td>
                      <td className="py-2 text-right font-bold text-[#0A0F1C]">{money(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, color, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2" style={{ color }}>{icon}<span className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">{label}</span></div>
      <div className="text-2xl font-extrabold text-[#0A0F1C]">{value}</div>
    </div>
  );
}
